import { Box, CircularProgress, DialogContentText, Grid, Slider } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { useAppSelector } from "hooks"
import Papa from "papaparse"
import { useState } from "react"
import organizationService from "services/organizationService"
import type { IOrganizationUserReport, IOrganizationUserReportCSV } from "types/reportTypes"
import TimeSpan from "types/timeSpan"

type Props = {
  open: boolean
  handleClose: () => void
}

export function OrganizationUsersReportDialog({ open, handleClose }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [reportPeriod, setReportPeriod] = useState<number>(30)

  const currentOrganization = useAppSelector((state) => state.organization.currentOrganization)

  const sliderValues = [
    {
      value: 7,
      label: "30 days",
      period: 30,
    },
    {
      value: 20,
      label: "90 days",
      period: 90,
    },
    {
      value: 40,
      label: "180 days",
      period: 180,
    },
    {
      value: 70,
      label: "1 year",
      period: 360,
    },
    {
      value: 100,
      label: "3 years",
      period: 1080,
    },
  ]

  const sliderOnChange = (_event: Event, value: number | number[], _activeThumb: number) => {
    var period = sliderValues.find((x) => x.value === value)

    if (period === undefined) {
      setReportPeriod(30)
    } else {
      setReportPeriod(period.period)
    }
  }

  const getOrganizationReport = () => {
    if (currentOrganization) {
      setIsLoading(true)
      organizationService
        .getOrganizationUsersReport(currentOrganization.id, reportPeriod)
        .then((organizationUsersReport) => {
          setIsLoading(false)
          downloadReport(organizationUsersReport.userReports)
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    }
  }

  const downloadReport = (userReports: IOrganizationUserReport[]) => {
    var userReportsCSV: IOrganizationUserReportCSV[] = userReports.map((userReport) => {
      var userTrainigTime = new TimeSpan(userReport.totalTrainingTimeInSeconds * 1000)

      const report: IOrganizationUserReportCSV = {
        Firstname: userReport.firstname,
        Lastname: userReport.lastname,
        UserGroup: userReport.userGroup,
        PIN: userReport.pin,
        UserCreated: new Date(userReport.createdDate).toLocaleDateString(),
        ActiveLicense: userReport.activeLicense,
        TotalTrainingTime: userTrainigTime.Format(),
        LastActiveDate: new Date(userReport.lastActiveDate).toLocaleString(),
        CompletedLessons: userReport.completedLessons,
        CompletedDistinctLessons: userReport.completedDistinctLessons,
        ExamsTaken: userReport.examsTaken,
        ExamsPassed: userReport.examsPassed,
      }

      return report
    })

    var reportName = `hiskill_users_report_${reportPeriod}.csv`

    var csv = Papa.unparse(userReportsCSV)
    var csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    var csvURL = window.URL.createObjectURL(csvData)

    var tempLink = document.createElement("a")
    tempLink.href = csvURL
    tempLink.setAttribute("download", reportName)
    tempLink.click()
  }

  return (
    <div>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{"Users report"}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText paddingX={2}>
            Choose the desired reporting period. Reports are updated every six hours for the latest
            information.
          </DialogContentText>
          <Grid paddingY={2} paddingX={2}>
            <Slider
              aria-label="Custom marks"
              defaultValue={7}
              step={null}
              valueLabelDisplay="off"
              marks={sliderValues}
              onChange={sliderOnChange}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button data-testid="cancelButton" onClick={handleClose}>
            Cancel
          </Button>
          <Box sx={{ m: 1, position: "relative" }}>
            <Button variant="contained" disabled={isLoading} onClick={getOrganizationReport}>
              Generate
            </Button>
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  )
}
